import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bounce, toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { joinCommunity, leaveCommunity } from "../../api/communityApi";
import { CommunityConfirmModal } from "./CommunityUI";

const TOAST_OPTIONS = { position: "top-center", autoClose: 3000, transition: Bounce };

/**
 * Join / switch / leave with a confirmation step. Render `modal` once in the
 * page; call `requestJoin(community)` or `requestLeave()` from buttons.
 * `onChange` runs after a successful join or leave.
 */
export default function useCommunityMembership({ onChange } = {}) {
  const { t } = useTranslation();
  const { userData, community: myCommunity, setCommunity } = useAuth();
  const [pending, setPending] = useState(null); // { action: "join" | "leave", community }
  const [busy, setBusy] = useState(false);

  const close = useCallback(() => setPending(null), []);
  const requestJoin = useCallback((community) => setPending({ action: "join", community }), []);
  const requestLeave = useCallback(() => setPending({ action: "leave", community: myCommunity }), [myCommunity]);

  const confirm = async () => {
    if (!pending || !userData?.id) return;
    setBusy(true);
    try {
      if (pending.action === "join") {
        const isSwitch = Boolean(myCommunity && myCommunity.id !== pending.community.id);
        const response = await joinCommunity(pending.community.id, userData.id, { switchCommunity: isSwitch });
        setCommunity(response.community);
        toast.success(
          t("communityJoinedToast", { defaultValue: "Welcome to {{name}}!", name: response.community.name }),
          TOAST_OPTIONS
        );
      } else {
        await leaveCommunity(userData.id);
        setCommunity(null);
        toast.success(
          t("communityLeftToast", { defaultValue: "You left {{name}}.", name: pending.community?.name }),
          TOAST_OPTIONS
        );
      }
      setPending(null);
      onChange?.();
    } catch (error) {
      // The stored community can be stale (e.g. joined from another device).
      if (error.body?.code === "ALREADY_IN_COMMUNITY" && error.body.currentCommunity) {
        setCommunity(error.body.currentCommunity);
      }
      toast.error(error.message, TOAST_OPTIONS);
    } finally {
      setBusy(false);
    }
  };

  let modal = null;
  if (pending?.action === "join") {
    const isSwitch = Boolean(myCommunity && myCommunity.id !== pending.community.id);
    modal = (
      <CommunityConfirmModal
        title={t("joinCommunityTitle", { defaultValue: "Join {{name}}?", name: pending.community.name })}
        body={
          isSwitch
            ? t("switchCommunityBody", {
                defaultValue:
                  "You can only be in one community. You'll leave {{current}} and join {{name}}.",
                current: myCommunity.name,
                name: pending.community.name,
              })
            : t("joinCommunityBody", {
                defaultValue: "{{name}} will show on your profile. You can be in one community at a time.",
                name: pending.community.name,
              })
        }
        confirmLabel={
          isSwitch
            ? t("switchCommunity", { defaultValue: "Switch community" })
            : t("joinCommunity", { defaultValue: "Join Community" })
        }
        busy={busy}
        onConfirm={confirm}
        onClose={close}
      />
    );
  } else if (pending?.action === "leave") {
    modal = (
      <CommunityConfirmModal
        title={t("leaveCommunityTitle", { defaultValue: "Leave {{name}}?", name: pending.community?.name })}
        body={t("leaveCommunityBody", {
          defaultValue: "It will be removed from your profile. You can join again any time.",
        })}
        confirmLabel={t("leaveCommunity", { defaultValue: "Leave community" })}
        danger
        busy={busy}
        onConfirm={confirm}
        onClose={close}
      />
    );
  }

  return { requestJoin, requestLeave, modal };
}
